import {
  AloftDateJSON,
  CompType,
  ListingType,
  PropertyData,
  ToolkitListingID,
} from '@aloft/toolkit-shared';

import { useAutofillProperty } from '../../../hooks/useAutofillProperty.ts';
import { CompSelectionState } from '../types.ts';
import { listingToPropertyData } from './listingToPropertyData.ts';

const addUADRatingsIfMissing = (
  property: PropertyData,
  selection: CompSelectionState[string] | undefined,
): PropertyData => ({
  ...property,
  editableData: {
    ...property.editableData,
    improvements: {
      ...property.editableData.improvements,
      aiUadQuality: property.editableData.improvements.aiUadQuality ?? selection?.uadQualityRating,
      aiUadCondition:
        property.editableData.improvements.aiUadCondition ?? selection?.uadConditionRating,
      uadQuality: property.editableData.improvements.uadQuality ?? selection?.uadQualityRating,
      uadCondition:
        property.editableData.improvements.uadCondition ?? selection?.uadConditionRating,
      craftsmanQuality:
        property.editableData.improvements.craftsmanQuality ?? selection?.uadQualityRating,
    },
  },
});

export const handleAddClickForCompCreation = async ({
  isLoading,
  rowSelection,
  createComps,
  autofillProperty,
  effectiveDate,
  orderID,
  isInspectifyOrder,
  hasArvOption,
  listingType,
}: {
  isLoading: boolean;
  rowSelection: CompSelectionState;
  createComps: (comps: PropertyData[]) => void;
  autofillProperty: ReturnType<typeof useAutofillProperty>;
  effectiveDate: AloftDateJSON;
  orderID: string;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  listingType?: ListingType;
}) => {
  const listingToProp = listingToPropertyData(
    autofillProperty,
    effectiveDate,
    orderID,
    listingType,
  );

  // Note: Our query only returns properties of the same type as the subject, so using the subject's property type for all comps
  if (!isLoading) {
    if (isInspectifyOrder && hasArvOption) {
      // New behavior: create separate comps for each selected comp type (AIV/ARV)
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter(
          (c) => c.listingData && (c.listingData?.ToolkitListingID || c.listingData?.ListingId),
        )
        .map((c) => ({
          listing: c.listingData,
          id: c.listingData?.ToolkitListingID || (c.listingData?.ListingId as ToolkitListingID),
        }))
        .filter(({ id }) => {
          const selection = rowSelection[id];
          return selection && (selection.aiv || selection.arv);
        })
        .flatMap(({ id, listing }) => {
          const selection = rowSelection[id];
          if (!selection) return [];

          const compTypes: CompType[] = [];
          if (selection.aiv) compTypes.push('aiv');
          if (selection.arv) compTypes.push('arv');

          // Create a separate PropertyData for each comp type selected
          return compTypes.map(async (compType) => {
            const property = addUADRatingsIfMissing(await listingToProp(listing!), selection);
            return { ...property, compType };
          });
        });

      createComps(await Promise.all(properties));
    } else {
      // Old behavior: create comps without compType for non-Inspectify orders or Inspectify orders without ARV option
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter(
          (c) => c.listingData && (c.listingData?.ToolkitListingID || c.listingData?.ListingId),
        )
        .map((c) => ({
          listing: c.listingData,
          id: c.listingData?.ToolkitListingID || (c.listingData?.ListingId as ToolkitListingID),
        }))
        .map(async ({ listing, id }) =>
          addUADRatingsIfMissing(await listingToProp(listing!), rowSelection[id]),
        );

      createComps(await Promise.all(properties));
    }
  }
};
